import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isDark, setIsDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Detect if we're on the home page (handles both "/" and empty)
  const isHome = location.pathname === '/' || location.pathname === '';

  // ===== THEME INITIALIZATION =====
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
      setIsDark(true);
    } else {
      document.documentElement.classList.remove('dark');
      setIsDark(false);
    }
  }, []);

  // ===== THEME TOGGLE =====
  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // ===== SYSTEM THEME LISTENER =====
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (!localStorage.getItem('theme')) {
        document.documentElement.classList.remove('dark');
        setIsDark(false);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // ===== SMOOTH SCROLL (only when on home page) =====
  useEffect(() => {
    if (!isHome) return;

    const handleClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (href === '#') return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [isHome]);

  // ===== SCROLL TO HASH ON LOAD (when arriving from another page) =====
  useEffect(() => {
    if (isHome && location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        // Slight delay to ensure layout is ready
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [isHome, location.hash]);

  // ===== BUILD ANCHOR HREF =====
  // On home page: "#tutorials" (smooth scroll)
  // On other pages: "/#tutorials" (navigate to home + scroll)
  const anchorHref = (hash) => (isHome ? `#${hash}` : `/#${hash}`);

  // ===== CLOSE MOBILE MENU WHEN ROUTE CHANGES =====
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  // ===== HANDLE NAV CLICK FOR NON-HOME PAGES =====
  // When on another page, clicking a hash link should navigate to home with hash
  const handleNavClick = (e, hash) => {
    if (!isHome) {
      e.preventDefault();
      navigate(`/#${hash}`);
    }
    setMobileMenuOpen(false);
  };

  // ===== ICONS =====
  const MoonIcon = () => (
    <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
  );

  const SunIcon = () => (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </>
  );

  return (
    <>
      <header className="fixed top-6 left-1/2 -translate-x-1/2 w-[90%] max-w-6xl z-50 _header">
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border border-gray-200/50 dark:border-gray-800/50 shadow-xl shadow-gray-200/20 dark:shadow-black/20 rounded-full px-6 py-3 flex items-center justify-between transition-colors duration-300">
          {/* Logo */}
          <a
            className="flex items-center gap-2"
            href={isHome ? '#home' : '/'}
            onClick={(e) => {
              if (!isHome) {
                e.preventDefault();
                navigate('/#home');
              } else {
                e.preventDefault();
                const target = document.querySelector('#home');
                if (target) target.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <span className="text-xl font-black text-blue-700 dark:text-blue-400 tracking-tighter">
              VLMS
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 font-semibold text-gray-600 dark:text-gray-300">
            <a
              className="text-blue-700 dark:text-blue-400 font-bold transition-colors"
              href={anchorHref('home')}
              onClick={(e) => handleNavClick(e, 'home')}
            >
              Início
            </a>
            <a
              className="hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
              href={anchorHref('reason')}
              onClick={(e) => handleNavClick(e, 'reason')}
            >
              Porquê VLMS?
            </a>
            <a
              className="hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
              href={anchorHref('tutorials')}
              onClick={(e) => handleNavClick(e, 'tutorials')}
            >
              Na Prática
            </a>
            <a
              className="hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
              href={anchorHref('plans')}
              onClick={(e) => handleNavClick(e, 'plans')}
            >
              Planos
            </a>
            <a
              className="hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
              href={anchorHref('faq')}
              onClick={(e) => handleNavClick(e, 'faq')}
            >
              FAQ
            </a>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle Theme"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isDark ? <MoonIcon /> : <SunIcon />}
              </svg>
            </button>

            <button
              onClick={(e) => navigate('/test')}
              className="hidden sm:inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-md hover:shadow-lg"
            >
              Testar Grátis
            </button>

            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-gray-700 dark:text-white p-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 5h16" />
                <path d="M4 12h16" />
                <path d="M4 19h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU OVERLAY */}
      <div
        className={`fixed inset-0 z-[100] bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl flex flex-col transition-all duration-300 ${
          mobileMenuOpen
            ? 'opacity-100 pointer-events-auto translate-y-0'
            : 'opacity-0 pointer-events-none -translate-y-2'
        }`}
      >
        <div className="flex justify-between items-center p-8">
          <span className="text-2xl font-black text-blue-700 dark:text-blue-400">
            VLMS
          </span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-400"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <nav className="flex flex-col items-center justify-center flex-1 gap-8 text-2xl font-bold text-gray-800 dark:text-gray-100">
          <a
            href={anchorHref('home')}
            className="text-blue-700 dark:text-blue-400"
            onClick={(e) => handleNavClick(e, 'home')}
          >
            Início
          </a>
          <a
            href={anchorHref('features')}
            className="hover:text-blue-700 dark:hover:text-blue-400"
            onClick={(e) => handleNavClick(e, 'features')}
          >
            Recursos
          </a>
          <a
            href={anchorHref('tutorials')}
            className="hover:text-blue-700 dark:hover:text-blue-400"
            onClick={(e) => handleNavClick(e, 'tutorials')}
          >
            Na Prática
          </a>
          <a
            href={anchorHref('plans')}
            className="hover:text-blue-700 dark:hover:text-blue-400"
            onClick={(e) => handleNavClick(e, 'plans')}
          >
            Planos
          </a>
          <a
            href={anchorHref('faq')}
            className="hover:text-blue-700 dark:hover:text-blue-400"
            onClick={(e) => handleNavClick(e, 'faq')}
          >
            FAQ
          </a>
          <button
          
            className="mt-4 bg-blue-600 text-white px-8 py-4 rounded-full shadow-xl"
            onClick={(e) =>navigate('/test')}
          >
            Testar Grátis
          </button>
        </nav>
      </div>
    </>
  );
};

export default Header;